/** @format */
import {
  Auth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updatePassword,
  sendPasswordResetEmail,
  authState,
} from '@angular/fire/auth';
import { DbService } from './db.service';
import { AlertService } from './alert.service';
import { Observable, lastValueFrom, of, switchMap, take } from 'rxjs';
import { Injectable } from '@angular/core';
import Swal from 'sweetalert2';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  user$: Observable<any>;

  constructor(public alertService: AlertService, private db: DbService, private auth: Auth) {
    this.user$ = authState(auth).pipe(switchMap(user => (user ? this.db.doc$(`members/${user.uid}`) : of(null))));
    // .pipe(
    //   map((user) => {
    //     // 코드 필요한 경우
    //     return user;
    //   })
    // );
  }

  // email + password 가입
  registerUser(value) {
    return new Promise<any>((resolve, reject) => {
      createUserWithEmailAndPassword(this.auth, value.email, value.password)
        .then(success => resolve(success))
        .catch(error => {
          let code = error['code'];
          this.showErrorMessage(code);
          reject(code);
        });
    });
  }

  // email + password 로그인
  loginUser(value) {
    return new Promise<any>((resolve, reject) => {
      signInWithEmailAndPassword(this.auth, value.email, value.password)
        .then(success => resolve(success))
        .catch(error => {
          let code = error['code'];
          this.showErrorMessage(code);
          reject(code);
        });
    });
  }

  // 가입한 회원에게 이메일 인증 메일 발송
  sendEmailVerificationUser(user) {
    return new Promise((resolve, reject) => {
      if (this.auth.currentUser) {
        user.sendEmailVerification().then(
          success => resolve(success),
          error => {
            let code = error['code'];
            this.showErrorMessage(code);
            reject(code);
          }
        );
      }
    });
  }

  // 현재 currentUser 로그아웃
  logoutUser() {
    return new Promise((resolve, reject) => {
      signOut(this.auth).then(
        success => {
          localStorage.clear();

          resolve(true);
        },
        error => {
          let code = error['code'];
          this.showErrorMessage(code);
          reject(code);
        }
      );
    });
  }

  // 현재 currentUser 회원탈퇴 (주의사항 : currentUser가 최신이 아닐 경우 재 로그인 필요)
  exitUser() {
    return new Promise((resolve, reject) => {
      this.auth.currentUser
        .delete()
        .then(() => {
          localStorage.clear();
          resolve(true);
        })
        .catch(error => {
          let code = error['code'];
          this.showErrorMessage(code);

          reject(code);
        });
    });
  }

  // currentUser 정보 받아오기
  async getUser() {
    return await lastValueFrom(this.user$.pipe(take(1)));
  }

  // 관리자 정보 받아오기 (admin/info 문서에서)
  async getAdminUser() {
    try {
      const currentUser = this.auth.currentUser;
      if (!currentUser) {
        return null;
      }

      // admin/info 문서에서 사용자 정보 가져오기
      const adminInfo = await this.db.toDoc$('admin/info');
      if (!adminInfo) {
        return null;
      }

      // ADMIN 배열에서 사용자 찾기
      const adminUser = adminInfo.ADMIN?.find(admin => admin.uuid === currentUser.uid);
      if (adminUser) {
        return adminUser;
      }

      // PM 배열에서 사용자 찾기
      const pmUser = adminInfo.PM?.find(pm => pm.uuid === currentUser.uid);
      if (pmUser) {
        return pmUser;
      }

      return null;
    } catch (error) {
      console.error('Error getting admin user info:', error);
      return null;
    }
  }

  // currentUser uid 받아오기
  uid() {
    return this.auth.currentUser?.uid;
  }

  // currentUser 비밀번호 변경
  async changePassword(newPassword) {
    return new Promise((resolve, reject) => {
      updatePassword(this.auth.currentUser, newPassword)
        .then(success => {
          resolve(success);
        })
        .catch(error => {
          let code = error['code'];
          this.showErrorMessage(code);

          reject(code);
        });
    });
  }

  // 비밀번호 재설정 메일 발송
  sendPasswordReset(email) {
    return new Promise((resolve, reject) => {
      sendPasswordResetEmail(this.auth, email)
        .then(success => {
          resolve(success);
        })
        .catch(error => {
          let code = error['code'];
          this.showErrorMessage(code);

          reject(code);
        });
    });
  }

  // 관리자 계정 초기화
  async initializeAdminData() {
    try {
      const adminInfo = {
        ADMIN: [],
        PM: []
      };
      
      await this.db.updateAt('admin/info', adminInfo);
      return true;
    } catch (error) {
      console.error('관리자 데이터 초기화 오류:', error);
      return false;
    }
  }

  showErrorMessage(code) {
    switch (code) {
      // Firebase Error Messages
      case 'auth/account-exists-with-different-credential':
        this.errormsg(errorMessages.accountExistsWithDifferentCredential['title'], errorMessages.accountExistsWithDifferentCredential['subTitle']);
        break;
      case 'auth/invalid-credential':
        this.errormsg(errorMessages.invalidCredential['title'], errorMessages.invalidCredential['subTitle']);
        break;
      case 'auth/operation-not-allowed':
        this.errormsg(errorMessages.operationNotAllowed['title'], errorMessages.operationNotAllowed['subTitle']);
        break;
      case 'auth/user-disabled':
        this.errormsg(errorMessages.userDisabled['title'], errorMessages.userDisabled['subTitle']);
        break;
      case 'auth/user-not-found':
        this.errormsg(errorMessages.userEmailNotFound['title'], errorMessages.userEmailNotFound['subTitle']);
        break;
      case 'auth/wrong-password':
        this.errormsg(errorMessages.wrongPassword['title'], errorMessages.wrongPassword['subTitle']);
        break;
      case 'auth/invalid-email':
        this.errormsg(errorMessages.invalidEmail['title'], errorMessages.invalidEmail['subTitle']);
        break;
      case 'auth/email-already-in-use':
        this.errormsg(errorMessages.emailAlreadyInUse['title'], errorMessages.emailAlreadyInUse['subTitle']);
        break;
      case 'auth/weak-password':
        this.errormsg(errorMessages.weakPassword['title'], errorMessages.weakPassword['subTitle']);
        break;
      case 'auth/requires-recent-login':
        this.errormsg(errorMessages.requiresRecentLogin['title'], errorMessages.requiresRecentLogin['subTitle']);
        break;
      case 'auth/user-mismatch':
        this.errormsg(errorMessages.userMismatch['title'], errorMessages.userMismatch['subTitle']);
        break;
      case 'auth/provider-already-linked':
        this.errormsg(errorMessages.providerAlreadyLinked['title'], errorMessages.providerAlreadyLinked['subTitle']);
        break;
      case 'auth/credential-already-in-use':
        this.errormsg(errorMessages.credentialAlreadyInUse['title'], errorMessages.credentialAlreadyInUse['subTitle']);
        break;
      case 'auth/too-many-requests':
        this.errormsg(errorMessages.toManyrequests['title'], errorMessages.toManyrequests['subTitle']);
        break;
    }
  }

  //완료 메세지
  successmsg(title, text) {
    return Swal.fire({
      title,
      text,
      icon: 'success',
      confirmButtonText: '확인',
      customClass: {
        confirmButton: 'btn btn-success swal-alert-btn',
      },
    });
  }
  //에러 메세지
  errormsg(title, text) {
    return Swal.fire({
      title,
      text,
      icon: 'error',
      confirmButtonText: `닫기`,
      customClass: {
        confirmButton: 'btn btn-success swal-alert-btn',
      },
    });
  }
}
const errorMessages = {
  // Firebase의 auth 에러 메세지 정의
  accountExistsWithDifferentCredential: {
    title: '계정 안내!',
    subTitle: '이미 있는 계정입니다.',
  },
  invalidCredential: {
    title: '인증 오류!',
    subTitle: '로그인 인증에 오류가 발생했습니다.',
  },
  operationNotAllowed: {
    title: '로그인 실패!',
    subTitle: '로그인 과정에서 오류가 발생했습니다. 관리자에게 문의하시길 바랍니다.',
  },
  userDisabled: {
    title: '정지 계정!',
    subTitle: '정지된 계정입니다. 관리자에게 연락하시길 바랍니다.',
  },
  userNotFound: { title: '계정 없음!', subTitle: '해당 계정 정보가 없습니다.' },
  userEmailNotFound: {
    title: '가입된 이메일 주소가 아닙니다.',
    subTitle: '다시 확인해 주시기 바랍니다.',
  },
  wrongPassword: {
    title: '비밀번호 오류!',
    subTitle: '입력하신 비밀번호가 잘못되었습니다.다시 확인하시기 바랍니다.',
  },
  invalidEmail: {
    title: '이메일 오류!',
    subTitle: '입력하신 이메일 정보가 없습니다. 다시 확인하시기 바랍니다.',
  },
  emailAlreadyInUse: {
    title: '사용할 수 없는 이메일!',
    subTitle: '이미 사용중인 이메일입니다. 다시 확인하시기 바랍니다.',
  },
  weakPassword: {
    title: '비밀번호 경고!',
    subTitle: '입력하신 비밀번호가 보안에 취약합니다.',
  },
  requiresRecentLogin: {
    title: '인증 만료!',
    subTitle: '인증이 만료되었습니다. 다시 로그인 하시기 바랍니다.',
  },
  userMismatch: {
    title: '사용자 불일치!',
    subTitle: '다른 사용자의 인증정보입니다!',
  },
  providerAlreadyLinked: {
    title: 'Already Linked!',
    subTitle: 'Sorry, but your account is already linked to this credential.',
  },
  credentialAlreadyInUse: {
    title: '불가능 인증정보!',
    subTitle: 'Sorry, but this credential is already used by another user.',
  },
  toManyrequests: {
    title: '확인 횟수 초과!',
    subTitle: '잘못된 비밀번호로 비밀번호 확인 횟수가 초과 되었습니다. 잠시 후 다시 시도하시기 바랍니다.',
  },
};
